import type { CSSProperties } from "react";
import { Button, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { StayCalendarDay, StayCalendarEvent, StayCalendarKind } from "~/utils";

const WEEKDAY_KEYS = [
  "Stay_cal_sun",
  "Stay_cal_mon",
  "Stay_cal_tue",
  "Stay_cal_wed",
  "Stay_cal_thu",
  "Stay_cal_fri",
  "Stay_cal_sat",
] as const;

const KIND_LEGEND_KEY = {
  lease: "Stay_cal_legend_lease",
  stay: "Stay_cal_legend_stay",
  pending: "Stay_cal_legend_pending",
  confirmed: "Stay_cal_legend_confirmed",
  checkedIn: "Stay_cal_legend_checkedIn",
  completed: "Stay_cal_legend_completed",
  block: "Stay_cal_legend_block",
} as const;

const KIND_STYLE: Record<StayCalendarKind, CSSProperties> = {
  lease: {
    background: "#1677ff",
    color: "#fff",
    border: "1px solid #0958d9",
  },
  stay: {
    background: "#13c2c2",
    color: "#fff",
    border: "1px solid #08979c",
  },
  confirmed: {
    background: "#13c2c2",
    color: "#fff",
    border: "1px solid #08979c",
  },
  checkedIn: {
    background: "#237804",
    color: "#fff",
    border: "1px solid #135200",
  },
  pending: {
    background:
      "repeating-linear-gradient(45deg, #fff7e6, #fff7e6 4px, #ffe7ba 4px, #ffe7ba 8px)",
    color: "#ad4e00",
    border: "2px dashed #d46b08",
  },
  completed: {
    background: "#f0f0f0",
    color: "#434343",
    border: "1px solid #8c8c8c",
  },
  block: {
    background:
      "repeating-linear-gradient(-45deg, #f5f5f5, #f5f5f5 4px, #d9d9d9 4px, #d9d9d9 8px)",
    color: "#434343",
    border: "1px solid #8c8c8c",
  },
};

const KIND_MARK: Record<StayCalendarKind, string> = {
  lease: "L",
  stay: "C",
  pending: "P",
  confirmed: "C",
  checkedIn: "IN",
  completed: "D",
  block: "B",
};

const LEGEND_ORDER: StayCalendarKind[] = [
  "checkedIn",
  "confirmed",
  "pending",
  "completed",
  "lease",
  "block",
];

export function StayMonthCalendar({
  days,
  monthLabel,
  onPrevMonth,
  onNextMonth,
  onEventClick,
}: {
  days: StayCalendarDay[];
  monthLabel: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onEventClick: (event: StayCalendarEvent) => void;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Button onClick={onPrevMonth}>{t("Stay_cal_prev")}</Button>
        <Typography.Text strong>{monthLabel}</Typography.Text>
        <Button onClick={onNextMonth}>{t("Stay_cal_next")}</Button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 4,
        }}
      >
        {WEEKDAY_KEYS.map((key) => (
          <Typography.Text
            key={key}
            type="secondary"
            style={{ textAlign: "center", fontSize: 12 }}
          >
            {t(key)}
          </Typography.Text>
        ))}
        {days.map((day) => (
          <div
            key={day.date}
            style={{
              minHeight: 88,
              border: day.isToday ? "2px solid #1677ff" : "1px solid #f0f0f0",
              borderRadius: 4,
              padding: 4,
              background: day.inMonth ? "#fff" : "#fafafa",
              opacity: day.inMonth ? 1 : 0.55,
            }}
          >
            <Typography.Text
              strong={day.isToday}
              style={{ fontSize: 12, display: "block", marginBottom: 4 }}
            >
              {Number(day.date.slice(-2))}
              {day.isToday ? ` · ${t("Stay_today")}` : ""}
            </Typography.Text>
            {day.events.length === 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {t("Stay_cal_open")}
              </Typography.Text>
            ) : (
              day.events.map((event) => (
                <button
                  key={`${event.kind}-${event.id}`}
                  type="button"
                  onClick={() => onEventClick(event)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 2,
                    padding: "2px 4px",
                    fontSize: 11,
                    borderRadius: 2,
                    cursor: "pointer",
                    ...KIND_STYLE[event.kind],
                  }}
                >
                  {KIND_MARK[event.kind]} {event.label}
                </button>
              ))
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
        {LEGEND_ORDER.map((kind) => (
          <span key={kind} style={{ fontSize: 12 }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                marginRight: 4,
                verticalAlign: "middle",
                ...KIND_STYLE[kind],
              }}
            />
            {t(KIND_LEGEND_KEY[kind])}
          </span>
        ))}
      </div>
    </div>
  );
}
